import { zamanMs } from "./utils/zamanOtoritesi";
// Kompakt sayı biçiminin TEK kaynağı (gerekçe `formatCount` üstünde).
import { formatCompact } from "./myclipsv3/design/formatNumber";

// Firestore Timestamp / ISO metin / epoch sayı — üç biçim de bu modüle geliyor
// (gövdedeki `?.seconds` ve `toDate()` dallarından TÜRETİLDİ, tahmin değil).
export type ZamanGirdisi =
  | number
  | string
  // `toMillis` çağrı yerlerinden GELDİ (StoryModalDesktop/Mobile → `tarih`).
  // Gövde onu OKUMAZ — davranış aynen korunur; ama tip kodun üç yüzüne de
  // uymak zorunda olduğu için şekilde yer alır.
  | { seconds?: number; toDate?: () => Date; toMillis?: () => number }
  | null
  | undefined;

export const formatTimeAgo = (timestamp: ZamanGirdisi) => {
  if (!timestamp) return "";
  // ── ZAMAN OKUMA TEK OTORİTEYE BAĞLANDI (2026-08-25, ölçülerek) ─────────
  // Eski gövde kendi ayrıştırıcısını taşıyordu ve ÜÇ biçimi bozuyordu
  // (node ile ölçüldü, «2 saat önce» beklenirken):
  //     ham SANİYE sayısı        → «21 Ocak 1970»   (ms sanıp 1970'e düşürüyordu)
  //     { _seconds } (JSON'a dönmüş Timestamp) → «NaN gün önce»
  //     { toMillis() } (seconds'sız) → «NaN gün önce»
  // `_seconds` bu kod tabanında VARSAYIMSAL DEĞİL: `utils/zamanOtoritesi.ts`
  // o dalı, üç dosya gerçekten o biçimle karşılaştığı için tanıyor.
  //
  // Bu, projedeki SON kendi-ayrıştırıcısını taşıyan zaman okuyucusuydu;
  // otoriteye bağlanması yedinci kopyanın yazılmasını imkânsız kılar.
  //
  // ⚠️ DARALMA YOK: `zamanMs` eskiden DOĞRU çözülen her girdiyi aynı ana
  // çözer (kapı yedi biçimi de tek tek ölçüyor). Değişen yalnız yukarıdaki
  // üç bozuk çıktıdır. `zamanMs` çözemezse `0` döner → eski gövdenin
  // `Invalid Date`ine karşılık boş metin (K8: «NaN önce» yasak).
  const ms = zamanMs(timestamp);
  if (!ms) return "";
  const date = new Date(ms);
  // ── #888 — NEGATİF SÜRE KİLİDİ ────────────────────────────────────────
  // `Math.max(0, …)` süs değil: `useGoreliZamanTiki` bu metni artık saat
  // sıçramasında GERÇEKTEN tazeliyor, dolayısıyla saat GERİYE atladığında
  // (uyku dönüşü, NTP düzeltmesi, elle saat değişimi) `diff` negatife düşer
  // ve ekranda «-3600 saniye önce» belirirdi. Sanal dünya invariant'ı K8
  // (`gecersiz-goreli-zaman-yok`) tam olarak bunu yasaklıyor. Kardeş kopyalar
  // (`ReviewsList`, `Bildirimler`) bu kilidi ZATEN taşıyor; eksik olan burasıydı.
  //
  // ⚠️ Bu fonksiyon SAF kalır (timer YOK). Tazeleme sorumluluğu bilerek
  // dışarıda — `src/hooks/useGoreliZamanTiki.ts` TEK paylaşılan tik.
  const diff = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  if (diff < 60) return `${diff} saniye önce`;
  if (diff < 3600) return `${Math.floor(diff / 60)} dakika önce`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} saat önce`;
  if (diff > 604800)
    return date.toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" });
  return `${Math.floor(diff / 86400)} gün önce`;
};

/**
 * ── #1030 — ZAMAN DAMGASI TEK BİÇİM (akış denetimi P2-1) ──────────────────
 * Ölçülen hâl: AYNI gönderinin zamanı ÜÇ ayrı biçimde yazılıyordu —
 *   · kart mobil     `26 saniye önce`   (`formatTimeAgo`, uzun)
 *   · kart masaüstü  `21 DAKİKA ÖNCE`   (aynı metin + `text-transform:uppercase`)
 *   · gönderi detayı `1D` / `15S` / `230G`  (`PostDetailModalDesktop` yerel kopyası)
 * Üçüncüsü yalnız tutarsız değil, YANILTICIYDI: `D` = **dakika** demekti,
 * ama IG okuru `d`yi *day* okur → «1D» bir dakikalık gönderiyi bir günlük
 * gösteriyordu. Tek kısaltma ailesi (`sn · dk · sa · g · h`) bu belirsizliği
 * kaldırır; `h` = **hafta** ve gün ailesinden ayrı harf taşır.
 *
 * ⚠️ `formatTimeAgo` (uzun biçim) KALDIRILMADI: check-in listesi, bildirim
 * ve yorum satırları dışında da 6 çağrı yeri var; bu tur yalnız gönderi
 * kartı + gönderi detayını tek biçime çeker.
 *
 * Parametre `ZamanGirdisi | Date`: yorum satırları `Date` de üretiyor
 * (`addComment` iyimser dönüşünde `createdAt: new Date()`), `formatTimeAgo`
 * ise `Date`i tipinde HİÇ tanımıyordu. Bu genişleme davranış değil TİP
 * düzeltmesidir — `zamanMs` `Date`i zaten çözüyor (`deger instanceof Date`);
 * eksik olan yalnız şekildi. `ZamanGirdisi`i genişletmek yerine burada
 * daraltıldı: `formatTimeAgo`/`formatDateTR` sözleşmesi aynen kalsın.
 *
 * SAF kalır (timer YOK) — tazeleme `hooks/useGoreliZamanTiki` işidir.
 * `Math.max(0, …)` kilidi `formatTimeAgo` ile aynı gerekçeyle burada da var
 * (#888: saat geriye atlayınca «-3600 sn» yazıyordu; K8 bunu yasaklar).
 */
export const formatTimeAgoShort = (timestamp: ZamanGirdisi | Date) => {
  if (!timestamp) return "";
  // Kendi ayrıştırıcısını YAZMAZ — yedinci kopya doğmasın diye tek otorite.
  const ms = zamanMs(timestamp);
  if (!ms) return "";
  const diff = Math.max(0, Math.floor((Date.now() - ms) / 1000));
  if (diff < 60) return `${diff} sn`;
  const dk = Math.floor(diff / 60);
  if (dk < 60) return `${dk} dk`;
  const sa = Math.floor(dk / 60);
  if (sa < 24) return `${sa} sa`;
  const g = Math.floor(sa / 24);
  if (g < 7) return `${g} g`;
  const h = Math.floor(g / 7);
  if (h < 53) return `${h} h`;
  return `${Math.floor(g / 365)} y`;
};

/* ══ SAYAÇ BİÇİMİ — TEK TANIM (owner, 2026-09-14) ═══════════════════════════
   *"Aynı uygulamada `1.2M` ile `1,2 Mn` kullanılması tutarsız. Mevcut Türkçe
   profil biçimini ortaklaştırma yönünde ilerle."*

   ÖLÇÜLEN KUSUR: depoda kompakt sayı üreten DÖRT ayrı tanım vardı —
     1. burası (ASCII "1.2M")
     2. `src/utils/formatCount.ts` (ASCII, ".0" kırpmalı)
     3. `src/myclipsv3/design/formatNumber.ts` `formatCompact` (Türkçe "1,2 Mn")
     4. `ProfilePostViewerMobile.tsx` içindeki yerel `nf` (Türkçe)
   Aynı gönderinin aynı sayısı akış kartında "1.2M", profil görüntüleyicisinde
   "1,2 Mn" okunuyordu; ikisi yan yana ekranda görülebiliyordu.

   Karar: TÜRKÇE biçim (3) tek kaynak. Bu gövde ve (2) ona DEVREDER — böylece
   51 çağrı yeri tek satırla hizalanır, her çağrı yerini ayrı ayrı düzenlemek
   (ve birini atlamak) gerekmez.
   ⚠️ Boş/sayı-olmayan davranışı KORUNDU: burası "" döner (çağıranlar bunu
   "gösterme" diye okuyor), (2) "0" döner. Ortaklaşan şey BİÇİM, boşluk
   politikası değil. */
export const formatCount = (n: unknown) => {
  if (typeof n !== "number") return "";
  return formatCompact(n);
};

// Yerel `toMillis` KALDIRILDI — saniye/milisaniye ayracını `2e12`de tutuyordu
// ve bugünün epoch-ms'ini 1000 ile çarpıp 58617 yılına taşıyordu. Gövde tek
// otoriteye indirildi (ölçüm + gerekçe: `utils/zamanOtoritesi.ts`).
const toMillis = zamanMs;

// Sadece TARİH (saat yok) — dd.MM.yyyy
export const formatDateTR = (ts: ZamanGirdisi) => {
  const ms = toMillis(ts);
  if (!ms) return "";
  const d = new Date(ms);
  return d.toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" });
};
